<template>
  <div class="row bg-light py-2 border rounded">
    <div class="col-12">
      <div class="d-flex justify-content-between align-items-center">
        <IconRefresh
          size="20"
          class="text-success"
          style="cursor: pointer"
          @click="handleRefreshClick()"
        />
        <BFormGroup
          id="input-group-filter-start-date"
          label="Start Date:"
          label-for="filter-start-date"
          class="floating-group"
        >
          <BFormInput
            id="filter-start-date"
            v-model="fltrStartDate"
            type="date"
            size="sm"
            style="font-size: 0.85rem"
            :class="{ 'is-invalid': fltrsDateError !== null }"
            @change="onDateChange"
          />
        </BFormGroup>
        <BFormGroup
          id="input-group-filter-end-date"
          label="End Date:"
          label-for="filter-end-date"
          class="floating-group"
        >
          <BFormInput
            id="filter-end-date"
            v-model="fltrEndDate"
            type="date"
            size="sm"
            style="font-size: 0.85rem"
            :class="{ 'is-invalid': fltrsDateError !== null }"
            @change="onDateChange"
          />
        </BFormGroup>
        <BFormGroup
          id="input-group-selected-trsxns-sum"
          label="Trsxns Sum:"
          label-for="selected-trsxns-sum"
          class="floating-group"
        >
          <BFormInput
            id="selected-trsxns-sum"
            :modelValue="selectedTrsxnsSum"
            type="number"
            step="0.01"
            size="sm"
            style="width: 100px; font-size: 0.85rem"
            readonly
          />
        </BFormGroup>
      </div>
    </div>
    <div class="col-12 pt-1">
      <div class="small d-flex justify-content-between align-items-center">
        <BFormGroup
          id="input-group-inst-name-uid-filter"
          label="Inst Name & UID:"
          label-for="institution-name-uid-filter"
          class="floating-group"
        >
          <BFormSelect
            id="institution-name-uid-filter"
            v-model="selectedInstNameUid"
            :options="instNameUidOpts"
            size="sm"
            style="font-size: 0.85rem"
          />
        </BFormGroup>
        <IconDeviceFloppy
          size="20"
          class="text-success"
          style="cursor: pointer"
          @click="handlePersistSelectedTrsxns()"
        />
      </div>
      <div class="col-12">
        <span v-if="fltrsDateError" class="small text-danger">{{
          fltrsDateError
        }}</span>
      </div>
      <div class="col-12 pt-2">
        <div v-for="txn in sortedTrsxns" :key="txn.id">
          <IconCancel
            v-if="!txn.snapshot_categories"
            v-b-tooltip.click="
              `Make sure to assign its spending categories first`
            "
            size="13"
            class="text-danger mb-1"
            style="cursor: pointer"
          />
          <IconSquareOff
            v-else-if="txn.parent_trsxn_uid !== null"
            v-b-tooltip.click="
              `Transaction is already linked to a parent transaction.`
            "
            size="13"
            class="text-warning mb-1"
            style="cursor: pointer"
          />
          <IconSquareCheck
            v-else-if="selectedTrsxns[txn.transaction_uid]"
            size="13"
            class="text-success mb-1"
            style="cursor: pointer"
            @click="handleTransactionSelection(txn)"
          />
          <IconSquare
            v-else
            size="13"
            class="mb-1"
            style="cursor: pointer"
            @click="handleTransactionSelection(txn)"
          />
          {{ txn.date }}: <strong>{{ txn.amount }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  BFormGroup,
  BFormInput,
  vBTooltip,
  BFormSelect,
} from "bootstrap-vue-next";
import {
  IconRefresh,
  IconCancel,
  IconSquare,
  IconSquareCheck,
  IconDeviceFloppy,
  IconSquareOff,
} from "@tabler/icons-vue";
import { formatDateToyyyyMMdd } from "@/utils/utils.js";

export default {
  name: "TransactionsIdentifyPaymentTrsxnsView",
  props: {
    txn: {
      type: Object,
      required: true,
    },
  },
  directives: {
    BTooltip: vBTooltip,
  },
  components: {
    BFormGroup,
    BFormInput,
    BFormSelect,
    IconRefresh,
    IconCancel,
    IconSquare,
    IconSquareCheck,
    IconDeviceFloppy,
    IconSquareOff,
  },
  data() {
    return {
      selectedInstNameUid: null,
      fltrStartDate: null,
      fltrEndDate: null,
      fltrsDateError: null,
      transactions: null,
      selectedTrsxns: {},
    };
  },
  computed: {
    sortedTrsxns() {
      if (!this.filteredTrsxns) return [];
      return [...this.filteredTrsxns].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
    },
    filteredTrsxns() {
      if (!this.transactions) return [];
      if (!this.selectedInstNameUid) return this.transactions;

      const filtered = [];
      const selectedInstName = this.selectedInstNameUid.split(" (")[0];
      const selectedInstUid = this.selectedInstNameUid
        .split(" (")[1]
        ?.slice(0, -1);
      for (const txn of this.transactions) {
        if (txn.institution_name !== selectedInstName) continue;
        if (txn.institution_uid !== selectedInstUid) continue;
        // if (Math.abs(txn.amount - this.txn.amount) < Number.EPSILON) continue;
        filtered.push(txn);
      }

      return filtered;
    },
    selectedTrsxnsSum() {
      return Object.values(this.selectedTrsxns)
        .reduce((sum, txn) => sum + txn.amount, 0)
        .toFixed(2);
    },
    instNameUidOpts() {
      const opts = [{ value: null, text: "— Select —", disabled: true }];

      if (!this.transactions) return opts;

      const instNameUidSet = new Set(
        this.transactions.map((txn) =>
          this.formatInstNameUid(txn.institution_name, txn.institution_uid)
        )
      );

      const instNameUidOpts = Array.from(instNameUidSet).map((instNameUid) => ({
        value: instNameUid,
        text: instNameUid,
      }));

      return opts.concat(instNameUidOpts);
    },
  },
  created() {
    // Initialize the filterEndDate to the transaction's date. Then, set the fltrStartDate to 45 days before that.
    this.fltrEndDate = this.txn.date;
    const startDateObj = new Date(this.fltrEndDate);
    startDateObj.setDate(startDateObj.getDate() - 45);
    this.fltrStartDate = formatDateToyyyyMMdd(startDateObj, "-");

    this.selectedInstNameUid = this.formatInstNameUid(
      this.txn.institution_name,
      this.txn.institution_uid
    );
  },
  methods: {
    formatInstNameUid(instName, instUid) {
      return `${instName} (${instUid})`;
    },
    onDateChange() {
      this.fltrsDateError = null;
      this.fetchTransactionsByDateRange();
    },
    validateTransactionsDateFilters() {
      if (!this.fltrStartDate) {
        this.fltrsDateError = "Start date is required.";
        return false;
      }

      if (!this.fltrEndDate) {
        this.fltrsDateError = "End date is required.";
        return false;
      }

      const start = new Date(this.fltrStartDate);
      const end = new Date(this.fltrEndDate);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);

      if (diffDays < 0) {
        this.fltrsDateError = "End date cannot be before start date.";
        return false;
      }

      if (diffDays > 61) {
        this.fltrsDateError = "Date range cannot exceed 61 days.";
        return false;
      }

      this.fltrsDateError = null;
      return true;
    },
    async fetchTransactionsByDateRange() {
      if (!this.validateTransactionsDateFilters()) return;

      if (!this.txn || !this.txn.acct_uid) {
        console.warn(
          "No account selected, cannot fetch transactions. Please select an account first."
        );
        return;
      }

      this.transactions = null; // Set to null to show loading state
      try {
        const response = await this.$api.get(
          "/banking-institutions/transactions",
          {
            params: {
              filter: [
                `acctUid:eq:${this.txn.acct_uid}`,
                `date:gte:${this.fltrStartDate}`,
                `date:lte:${this.fltrEndDate}`,
              ],
            },
          }
        );
        this.transactions = response.data.map((txn) => txn.item);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    },
    resetSelectedTrsxns() {
      this.selectedTrsxns = {};
      this.$emit("updateSelectedTrsxns", this.selectedTrsxns);
    },
    handleRefreshClick() {
      this.selectedInstNameUid = this.formatInstNameUid(
        this.txn.institution_name,
        this.txn.institution_uid
      ); // Reset institution filter to default
      this.resetSelectedTrsxns();
      this.fetchTransactionsByDateRange();
    },
    handleTransactionSelection(txn) {
      if (this.selectedTrsxns[txn.transaction_uid]) {
        delete this.selectedTrsxns[txn.transaction_uid];
      } else {
        this.selectedTrsxns[txn.transaction_uid] = txn;
      }
      // Purposely not emitting the update here to avoid excessive emissions while the user is selecting/deselecting transactions. Instead, the user can click the save icon to persist the selected transactions, which will emit the update then.
    },
    // This method takes a look at the transactions and tries to identify which ones are part of the payment by adding and subtracting amounts.
    // Once it identifies the transactions that are likely part of the payment, it looks at their spending categories and adds them up (each category separately), so that said categories are the ones attached to the payment transaction.
    // Then, it surfaces those to the user for confirmation
    handleSmartAssignment() {},
    handlePersistSelectedTrsxns() {
      this.$emit("updateSelectedTrsxns", this.selectedTrsxns);
    },
  },
};
</script>
